import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BellOff,
  BellRing,
  KeyRound,
  Share,
  ShieldCheck,
  ShieldOff,
  SquarePlus,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/misc';
import { apiGet, apiPatch, ApiRequestError } from '@/lib/api';
import { PushSetupError, usePushNotifications } from '@/hooks/use-push';
import { FormError } from './auth-layout';
import {
  useBegin2fa,
  useChangePassword,
  useConfirm2fa,
  useDisable2fa,
  useSession,
} from './use-session';
import type { NotificationChannel, NotificationPreferenceRow } from '@/lib/types';

const TABS = [
  ['profile', 'Profile'],
  ['security', 'Security'],
  ['notifications', 'Notifications'],
] as const;

type Tab = (typeof TABS)[number][0];

export function AccountPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) ?? 'profile';
  const { data: user } = useSession();

  return (
    <div>
      <PageHeader title="My account" description={user?.email} />

      <div className="mb-5 flex gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'notifications' && <NotificationsTab />}
    </div>
  );
}

// ── Profile ──────────────────────────────────────────────────────────────────

function ProfileTab() {
  const { data: user } = useSession();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>
          Your administrator manages these details. Ask them if something needs changing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">
          <Detail label="Name" value={user?.name} />
          <Detail label="Email" value={user?.email} />
          <Detail label="Job title" value={user?.designation ?? '—'} />
          <Detail label="Department" value={user?.department ?? '—'} />
          <Detail label="Timezone" value={user?.timezone} />
          <Detail label="Organisation" value={user?.organization?.name} />
          <div>
            <dt className="text-xs text-muted-foreground">Roles</dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {user?.roles?.map((role) => (
                <Badge key={role.id} variant="secondary">
                  {role.name}
                </Badge>
              ))}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Teams</dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {user?.teams?.length ? (
                user.teams?.map((team) => (
                  <Badge key={team.id} variant="outline">
                    {team.name}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

// ── Security ─────────────────────────────────────────────────────────────────

function SecurityTab() {
  const { data: user } = useSession();
  const changePassword = useChangePassword();
  const begin2fa = useBegin2fa();
  const confirm2fa = useConfirm2fa();
  const disable2fa = useDisable2fa();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [setup, setSetup] = useState<{ qrDataUrl: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [disablePassword, setDisablePassword] = useState('');

  const submitPassword = async () => {
    setPasswordError(null);
    try {
      await changePassword.mutateAsync({ currentPassword: current, newPassword: next });
    } catch (caught) {
      setPasswordError(
        caught instanceof ApiRequestError ? caught.message : 'Could not change your password.',
      );
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4" />
            Password
          </CardTitle>
          <CardDescription>
            Changing your password signs you out of every device, including this one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="current">Current password</Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="next">New password</Label>
              <Input
                id="next"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                At least 10 characters, with upper and lower case and a number.
              </p>
            </div>
          </div>

          <FormError message={passwordError} />

          <Button
            onClick={submitPassword}
            disabled={changePassword.isPending || !current || next.length < 10}
          >
            {changePassword.isPending ? 'Updating…' : 'Change password'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {user?.twoFactorEnabled ? (
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
            ) : (
              <ShieldOff className="h-4 w-4 text-muted-foreground" />
            )}
            Two-factor authentication
          </CardTitle>
          <CardDescription>
            {user?.twoFactorEnabled
              ? 'On. You will be asked for a code from your authenticator app each time you sign in.'
              : 'Add a second step to your sign-in using an authenticator app.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {recoveryCodes && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-900">
                Save these recovery codes somewhere safe
              </p>
              <p className="mt-1 text-xs text-amber-800">
                Each one works once if you lose your phone. They are shown only now — we keep hashes,
                not the codes.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-1.5 font-mono text-sm sm:grid-cols-4">
                {recoveryCodes.map((recoveryCode) => (
                  <span key={recoveryCode} className="rounded bg-white px-2 py-1 text-center">
                    {recoveryCode}
                  </span>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => setRecoveryCodes(null)}
              >
                I have saved them
              </Button>
            </div>
          )}

          {user?.twoFactorEnabled ? (
            !user.organization.enforce2fa && (
              <div className="space-y-3">
                <div className="space-y-1.5 sm:max-w-xs">
                  <Label htmlFor="disable-password">Confirm your password to turn it off</Label>
                  <Input
                    id="disable-password"
                    type="password"
                    value={disablePassword}
                    onChange={(event) => setDisablePassword(event.target.value)}
                  />
                </div>
                <FormError message={twoFactorError} />
                <Button
                  variant="outline"
                  disabled={disable2fa.isPending || !disablePassword}
                  onClick={async () => {
                    setTwoFactorError(null);
                    try {
                      await disable2fa.mutateAsync({ password: disablePassword });
                      setDisablePassword('');
                    } catch (caught) {
                      setTwoFactorError(
                        caught instanceof ApiRequestError ? caught.message : 'Could not turn it off.',
                      );
                    }
                  }}
                >
                  Turn off two-factor
                </Button>
              </div>
            )
          ) : setup ? (
            <div className="space-y-3">
              <p className="text-sm">
                Scan this with Google Authenticator, 1Password or any TOTP app, then enter the code
                it shows.
              </p>
              <img
                src={setup.qrDataUrl}
                alt="Two-factor QR code"
                className="h-44 w-44 rounded-md border bg-white p-2"
              />
              <p className="text-xs text-muted-foreground">
                Cannot scan? Enter this key manually:{' '}
                <code className="font-mono">{setup.secret}</code>
              </p>
              <div className="space-y-1.5 sm:max-w-[12rem]">
                <Label htmlFor="totp-code">6-digit code</Label>
                <Input
                  id="totp-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
              <FormError message={twoFactorError} />
              <div className="flex gap-2">
                <Button
                  disabled={confirm2fa.isPending || code.length !== 6}
                  onClick={async () => {
                    setTwoFactorError(null);
                    try {
                      const result = await confirm2fa.mutateAsync({ totp: code });
                      setRecoveryCodes(result.recoveryCodes);
                      setSetup(null);
                      setCode('');
                    } catch (caught) {
                      setTwoFactorError(
                        caught instanceof ApiRequestError
                          ? caught.message
                          : 'That code did not work.',
                      );
                    }
                  }}
                >
                  {confirm2fa.isPending ? 'Verifying…' : 'Turn on two-factor'}
                </Button>
                <Button variant="outline" onClick={() => setSetup(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              disabled={begin2fa.isPending}
              onClick={async () => {
                setTwoFactorError(null);
                const result = await begin2fa.mutateAsync();
                setSetup(result);
              }}
            >
              {begin2fa.isPending ? 'Preparing…' : 'Set up two-factor'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Notifications ────────────────────────────────────────────────────────────

function NotificationsTab() {
  const queryClient = useQueryClient();

  const { data: preferences = [] } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => apiGet<NotificationPreferenceRow[]>('/notifications/preferences'),
  });

  const update = useMutation({
    mutationFn: (body: {
      type: string;
      channel: NotificationChannel;
      enabled: boolean;
      digest: string;
    }) => apiPatch('/notifications/preferences', body),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['notification-preferences'] }),
  });

  const groups = [...new Set(preferences.map((preference) => preference.group))];

  return (
    <div className="space-y-5">
      <PushDeviceCard />

      {groups.map((group) => (
        <Card key={group}>
          <CardHeader>
            <CardTitle className="text-base">{group}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {preferences
              .filter((preference) => preference.group === group)
              .map((preference) => (
                <div
                  key={preference.type}
                  className="flex flex-wrap items-center justify-between gap-3 border-b py-2.5 last:border-0"
                >
                  <span className="text-sm">{preference.label}</span>
                  <div className="flex items-center gap-4">
                    {preference.channels.map((channel) => (
                      <label
                        key={channel.channel}
                        className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground"
                      >
                        {channel.channel === 'IN_APP' ? 'In app' : 'Email'}
                        <Switch
                          checked={channel.enabled}
                          onCheckedChange={(enabled) =>
                            update.mutate({
                              type: preference.type,
                              channel: channel.channel,
                              enabled,
                              digest: channel.digest,
                            })
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Browser push for the device in hand. The preferences below decide what is
 * sent; this decides whether this phone or laptop is a place it arrives.
 */
function PushDeviceCard() {
  const push = usePushNotifications();
  const [error, setError] = useState<string | null>(null);

  const status = push.needsInstallOnIos
    ? 'On iPhone and iPad, notifications only reach apps on the Home Screen. Add the portal there and open it from its icon to turn them on.'
    : !push.supported
      ? 'This browser cannot receive push notifications.'
      : push.permission === 'denied'
        ? 'Blocked. Allow notifications for this site in your browser settings, then come back here.'
        : push.enabled
          ? 'On. This device hears about new activity even while the portal is closed.'
          : push.permission === 'granted'
            ? 'Allowed, but this device is not registered yet.'
            : 'Off. Get told on this device when something needs you.';

  const canToggle = push.supported && !push.needsInstallOnIos && push.permission !== 'denied';

  const toggle = async () => {
    setError(null);
    try {
      if (push.enabled) await push.disable();
      else await push.enable();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError || caught instanceof PushSetupError
          ? caught.message
          : 'Could not update notifications on this device.',
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {push.enabled ? (
            <BellRing className="h-4 w-4 text-emerald-600" />
          ) : (
            <BellOff className="h-4 w-4 text-muted-foreground" />
          )}
          Notifications on this device
        </CardTitle>
        <CardDescription>{status}</CardDescription>
      </CardHeader>
      {(canToggle || push.needsInstallOnIos || error) && (
        <CardContent className="space-y-3">
          {push.needsInstallOnIos && (
            <ol className="space-y-1.5 text-sm">
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Share className="h-3.5 w-3.5" />
                </span>
                Tap <span className="font-medium">Share</span> in Safari.
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <SquarePlus className="h-3.5 w-3.5" />
                </span>
                Choose <span className="font-medium">Add to Home Screen</span>.
              </li>
            </ol>
          )}

          <FormError message={error} />

          {canToggle && (
            <Button
              variant={push.enabled ? 'outline' : 'default'}
              disabled={push.busy}
              onClick={toggle}
            >
              {push.busy ? 'Working…' : push.enabled ? 'Turn off on this device' : 'Turn on'}
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium">{value ?? '—'}</dd>
    </div>
  );
}
